import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
// Angular
import { ErrorHandler, ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslatePlaceholderDynamicAlias, TranslateStaticAlias } from './Common/Directives/translation';
import { ViewGuard } from './Guards/view.guard';
import { HttpRequestInterceptor } from './Interceptors/http-request.interceptor';
import { AuthServiceV2 } from './Services/api/v2/auth.service';
import { BasketService } from './Services/api/v2/basket.service';
import { RateService } from './Services/api/v2/rate.service';
import { BasketStoreService } from './Services/stores/basket.store';

// Modules

import { Doors } from './Classes/doors.class';
// Custom classes
import { SpaContext } from './Classes/spacontext';
import { ConfirmLeaveGuard } from './Guards/confirm-leave.guard';

// Directives

import { PageLoadHandler } from './Classes/PageLoadHandler';
import { VisibaApiHttp } from './Classes/visibaHttp.class';
import { SpaLogger } from './Common/Classes/Global/spalogger.class';
import { ImageViewerComponent } from './Common/Components/imageviewer.component';
import { ToggleSwitchComponent } from './Common/Components/toggle-switch.component';
import { AutoStick } from './Common/Directives/autoStick.directive';
import { Autosize } from './Common/Directives/autosize.directive';
import { AlignHeight } from './Common/Directives/html';
import { TrapFocusDirective } from './Common/Directives/trapFocus.directive';
import { VisibaAlertComponent } from './Common/Directives/visibaAlert';
import { VisibaDatepickerComponent } from './Common/Directives/visibaDatepicker';
import { VisibaGoogleChart } from './Common/Directives/visibaGoogleChart';
import { VisibaInputComponent } from './Common/Directives/visibaInput';
import { VisibaNationalIdentityNumberComponent } from './Common/Directives/visibaNationalIdentityNumber';
import { visibaProgressBar } from './Common/Directives/visibaProgressBar';
import { VisibaSaveButtonComponent } from './Common/Directives/visibaSaveButton';
import { VisibaSelectComponent } from './Common/Directives/visibaSelect';
import { VisibaSvg } from './Common/Directives/visibaSvg';
import { VisibaSvgIconRepository } from './Common/Directives/visibaSvgIconRepository';
import { VisibaTelComponent } from './Common/Directives/visibaTel';
import { VisibaTimepickerComponent } from './Common/Directives/visibaTimepicker';
import { Helper } from './Common/Helpers/helper';
import { ConsolePipe } from './Common/Pipes/consolePipe';
import { DateDisplayPipe } from './Common/Pipes/dateDisplayPipe';
import { SafeHtmlPipe } from './Common/Pipes/safeHtmlPipe';
import { TranslateAllowEmptyPipe, TranslatePipe } from './Common/Pipes/translate.pipe';
import { AudioPlayerService } from './Common/Services/audioPlayer.service';
import { TranslateService } from './Common/Services/translate.service';
import { ConfirmPhoneNumberComponent } from './Components/confirmPhoneNumber';
import { DynamicFormComponent } from './Components/DynamicForm/dynamicForm.component';
import { DfqMultiChoiceComponent } from './Components/DynamicForm/question-multichoice.component';
import { DfqMultilineComponent } from './Components/DynamicForm/question-multiline.component';
import { DfqSingleChoiceRadioComponent } from './Components/DynamicForm/question-singlechoiceradio.component';
import { DfqSingleChoiceSelectComponent } from './Components/DynamicForm/question-singlechoiceselect.component';
import { DfqYesNoComponent } from './Components/DynamicForm/question-yesno.component';
import { MediaTestMiniComponent } from './Components/mediaTestMini.component';
import { MessagingComponent } from './Components/messaging.component';
import { ModalComponent } from './Components/modal.component';
import { NotificationComplianceComponent } from './Components/notificationCompliance.component';
import { SemanticViewComponent } from './Components/semantic-view.component';
import { succesBox } from './Components/succesBox.component';
// Components
import { VisibaPaymentComponent } from './Components/visibaPayment.component';
import { VisibaStepComponent } from './Components/visibaStep.component';
import { ErrHandler } from './error.handler';
import { MessagingInjectable } from './Injectables/messaging.injectable';
import { AppointmentsServiceV2 } from './Services/api/v2/appointments.service';
import { CasesServiceV2 } from './Services/api/v2/cases.service';
import { DropInServiceV2 } from './Services/api/v2/dropin.service';
import { IdentityServiceV2 } from './Services/api/v2/identity.service';
import { MessagingServiceV2 } from './Services/api/v2/messaging.service';
import { NotificationSettingsService } from './Services/api/v2/notificationSettings.service';
import { RegistrationService } from './Services/api/v2/registration.service';
import { SmsAuthService } from './Services/api/v2/smsAuth.service';
import { TimeSlotLockApiService } from './Services/api/v2/timeslot_lock.service';
import { ConditionServiceV2 } from './Services/api/v2/Units/condition.service';
import { DropInUnitServiceV2 } from './Services/api/v2/Units/dropIn.service';
import { DynamicFormServiceV2 } from './Services/api/v2/Units/dynamic-form.service';
import { ExternalDataService } from './Services/api/v2/Units/external-data.service';
import { PaymentServiceV2 } from './Services/api/v2/Units/payment.service';
import { PhysicalLocationService } from './Services/api/v2/Units/physical-location.service';
import { TimeslotServiceV2 } from './Services/api/v2/Units/timeslot.service';
import { UnitService } from './Services/api/v2/Units/units.service';
import { WebContentButtonsService } from './Services/api/v2/Units/webContentButtons.service';
import { UnitApiService } from './Services/api/v2/units_api.service';
import { UsageAgreementsService } from './Services/api/v2/usageAgreements.service';
import { UserFilesService } from './Services/api/v2/userfiles.service';
import { ApplicationService } from './Services/application.service';
import { AuthContextService } from './Services/authContext.service';
import { DateService } from './Services/date.service';
import { IdentityFactoryService } from './Services/flexible_patient_buttons.service/identity_factory.service';
import { FlexiblePatientButtonsService } from './Services/flexible_patient_buttons.service/mod';
import { GeneralService } from './Services/general.service';
import { ModalService } from './Services/modalService';
import { NavigationGuardService } from './Services/navigation-guard.service';
import { DropInQueueService } from './Services/Queue/dropin-queue.service';
import { RedRobinService } from './Services/red-robin.service';
import { ScheduledDataService } from './Services/scheduled-data';
// Services
import { SpaEventService } from './Services/spaEvents.service';

@NgModule({
	imports: [CommonModule, FormsModule, HttpClientModule, ReactiveFormsModule, RouterModule],
	declarations: [
		// Directives
		VisibaSvg,
		Autosize,
		VisibaSelectComponent,
		VisibaDatepickerComponent,
		VisibaTimepickerComponent,
		VisibaTelComponent,
		VisibaNationalIdentityNumberComponent,
		VisibaInputComponent,
		AlignHeight,
		VisibaGoogleChart,
		VisibaPaymentComponent,
		MediaTestMiniComponent,
		ImageViewerComponent,
		ToggleSwitchComponent,
		VisibaStepComponent,
		AutoStick,
		TrapFocusDirective,
		DynamicFormComponent,
		DfqYesNoComponent,
		DfqMultiChoiceComponent,
		DfqMultilineComponent,
		DfqSingleChoiceRadioComponent,
		DfqSingleChoiceSelectComponent,
		VisibaSaveButtonComponent,
		MessagingComponent,
		succesBox,
		visibaProgressBar,
		VisibaAlertComponent,
		TranslateStaticAlias,
		TranslatePlaceholderDynamicAlias,
		ConfirmPhoneNumberComponent,
		NotificationComplianceComponent,
		ModalComponent,
		SemanticViewComponent,
		// Pipes
		DateDisplayPipe,
		SafeHtmlPipe,
		TranslatePipe,
		TranslateAllowEmptyPipe,
		ConsolePipe,
	],
	exports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		RouterModule,
		// Directives
		VisibaSvg,
		Autosize,
		VisibaSelectComponent,
		VisibaDatepickerComponent,
		VisibaTimepickerComponent,
		VisibaTelComponent,
		VisibaNationalIdentityNumberComponent,
		VisibaInputComponent,
		AlignHeight,
		VisibaGoogleChart,
		VisibaPaymentComponent,
		MediaTestMiniComponent,
		ImageViewerComponent,
		ToggleSwitchComponent,
		VisibaStepComponent,
		AutoStick,
		TrapFocusDirective,
		DynamicFormComponent,
		DfqYesNoComponent,
		DfqMultiChoiceComponent,
		DfqMultilineComponent,
		DfqSingleChoiceRadioComponent,
		DfqSingleChoiceSelectComponent,
		VisibaSaveButtonComponent,
		MessagingComponent,
		succesBox,
		visibaProgressBar,
		VisibaAlertComponent,
		TranslateStaticAlias,
		TranslatePlaceholderDynamicAlias,
		ConfirmPhoneNumberComponent,
		ModalComponent,
		SemanticViewComponent,
		// Pipes
		DateDisplayPipe,
		SafeHtmlPipe,
		TranslatePipe,
		TranslateAllowEmptyPipe,
		ConsolePipe,
	],
})
export class SharedModule {
	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	static forRoot(): ModuleWithProviders<any> {
		return {
			ngModule: SharedModule,
			providers: [
				// additional providers
				VisibaApiHttp,
				Helper,
				SpaEventService,

				GeneralService,
				UserFilesService,

				FlexiblePatientButtonsService,
				IdentityFactoryService,

				TranslateService,
				DropInQueueService,
				Doors,
				VisibaSvgIconRepository,
				MessagingInjectable,
				UnitService,
				WebContentButtonsService,
				ExternalDataService,
				PhysicalLocationService,
				RedRobinService,

				UnitApiService,

				// V2 API services
				DynamicFormServiceV2,
				AppointmentsServiceV2,
				DropInServiceV2,
				DropInUnitServiceV2,
				IdentityServiceV2,
				TimeslotServiceV2,
				MessagingServiceV2,
				PaymentServiceV2,
				CasesServiceV2,
				ConditionServiceV2,
				RateService,
				AuthServiceV2,
				TimeSlotLockApiService,
				UsageAgreementsService,
				RegistrationService,
				SmsAuthService,

				// APP services
				ScheduledDataService,

				AudioPlayerService,

				BasketService,
				BasketStoreService,

				ModalService,
				NavigationGuardService,
				NotificationSettingsService,

				AuthContextService,
				ApplicationService,
				DateService,

				{ provide: ViewGuard, useClass: ViewGuard },
				{ provide: ConfirmLeaveGuard, useClass: ConfirmLeaveGuard },
				{ provide: SpaContext, useClass: SpaContext },
				{ provide: SpaLogger, useClass: SpaLogger },
				{
					provide: HTTP_INTERCEPTORS,
					useClass: HttpRequestInterceptor,
					multi: true,
				},
				{ provide: ErrorHandler, useClass: ErrHandler },
				{ provide: PageLoadHandler, useClass: PageLoadHandler },
			],
		};
	}
}
